import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Loader2, User, Briefcase, Calendar, DollarSign, CreditCard, Send } from 'lucide-react';
import caseLabel from '../../utils/caseLabel';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function RecurringInvoiceModal({ invoice, onClose, onSave }) {
  const isEditing = !!invoice;

  const [formData, setFormData] = useState({
    contact_id: '',
    case_id: '',
    frequency: 'monthly',
    billing_day: 1,
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    description: '',
    amount: '',
    tax_rate: 0,
    payment_terms: 'Net 30',
    notes: '',
    auto_send: false,
    send_via_square: false
  });

  const [contacts, setContacts] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchContacts();
    if (invoice) {
      setFormData({
        contact_id: invoice.contact_id || '',
        case_id: invoice.case_id || '',
        frequency: invoice.frequency || 'monthly',
        billing_day: invoice.billing_day || 1,
        start_date: invoice.start_date || new Date().toISOString().split('T')[0],
        end_date: invoice.end_date || '',
        description: invoice.description || '',
        amount: invoice.amount || '',
        tax_rate: invoice.tax_rate || 0,
        payment_terms: invoice.payment_terms || 'Net 30',
        notes: invoice.notes || '',
        auto_send: invoice.auto_send || false,
        send_via_square: invoice.send_via_square || false
      });
      if (invoice.contact_id) {
        fetchCasesForContact(invoice.contact_id);
      }
    }
  }, [invoice]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/contacts`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page_size: 1000 }
      });
      setContacts(response.data.contacts || []);
    } catch (err) {
      console.error('Error fetching contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCasesForContact = async (contactId) => {
    if (!contactId) {
      setCases([]);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/cases/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { client_id: contactId, page_size: 100 }
      });
      setCases(response.data.cases || []);
    } catch (err) {
      console.error('Error fetching cases:', err);
      setCases([]);
    }
  };

  const handleContactChange = (e) => {
    const contactId = e.target.value;
    setFormData(prev => ({ ...prev, contact_id: contactId, case_id: '' }));
    fetchCasesForContact(contactId);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.contact_id) {
      setError('Please select a client');
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    if (!formData.description) {
      setError('Please enter a description');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        tax_rate: parseFloat(formData.tax_rate) || 0,
        billing_day: parseInt(formData.billing_day),
        case_id: formData.case_id || null,
        end_date: formData.end_date || null
      };

      if (isEditing) {
        await axios.put(
          `${API_URL}/api/recurring-invoices/${invoice.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API_URL}/api/recurring-invoices`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      onSave();
    } catch (err) {
      console.error('Error saving recurring invoice:', err);
      setError(err.response?.data?.detail || 'Failed to save recurring invoice');
    } finally {
      setSaving(false);
    }
  };

  const getDayOptions = () => {
    if (formData.frequency === 'weekly') {
      return [
        { value: 0, label: 'Monday' },
        { value: 1, label: 'Tuesday' },
        { value: 2, label: 'Wednesday' },
        { value: 3, label: 'Thursday' },
        { value: 4, label: 'Friday' },
        { value: 5, label: 'Saturday' },
        { value: 6, label: 'Sunday' }
      ];
    } else {
      return Array.from({ length: 28 }, (_, i) => ({
        value: i + 1,
        label: `Day ${i + 1}`
      }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <DollarSign className="text-blue-400" />
            {isEditing ? 'Edit Recurring Invoice' : 'New Recurring Invoice'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-700 rounded transition-colors"
          >
            <X size={20} className="text-slate-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-400 p-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Client Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              <User size={14} className="inline mr-1" />
              Client *
            </label>
            <select
              name="contact_id"
              value={formData.contact_id}
              onChange={handleContactChange}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            >
              <option value="">Select a client...</option>
              {contacts.map(contact => (
                <option key={contact.id} value={contact.id}>
                  {contact.contact_name}
                </option>
              ))}
            </select>
          </div>

          {/* Case Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              <Briefcase size={14} className="inline mr-1" />
              Case (Optional)
            </label>
            <select
              name="case_id"
              value={formData.case_id}
              onChange={handleChange}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
              disabled={!formData.contact_id || cases.length === 0}
            >
              <option value="">No specific case</option>
              {cases.map(c => (
                <option key={c.id} value={c.id}>
                  {caseLabel(c)}
                </option>
              ))}
            </select>
          </div>

          {/* Frequency and Day */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                <Calendar size={14} className="inline mr-1" />
                Frequency *
              </label>
              <select
                name="frequency"
                value={formData.frequency}
                onChange={handleChange}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Billing Day *
              </label>
              <select
                name="billing_day"
                value={formData.billing_day}
                onChange={handleChange}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
              >
                {getDayOptions().map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Start and End Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Start Date *
              </label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                End Date (Optional)
              </label>
              <input
                type="date"
                name="end_date"
                value={formData.end_date}
                onChange={handleChange}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Amount and Tax */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                <DollarSign size={14} className="inline mr-1" />
                Amount *
              </label>
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                min="0.01"
                step="0.01"
                placeholder="0.00"
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Tax Rate (%)
              </label>
              <input
                type="number"
                name="tax_rate"
                value={formData.tax_rate}
                onChange={handleChange}
                min="0"
                max="100"
                step="0.01"
                placeholder="0"
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              Description *
            </label>
            <input
              type="text"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="e.g., Monthly retainer fee"
              maxLength={500}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Payment Terms */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              Payment Terms
            </label>
            <select
              name="payment_terms"
              value={formData.payment_terms}
              onChange={handleChange}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
            >
              <option value="Due on Receipt">Due on Receipt</option>
              <option value="Net 15">Net 15</option>
              <option value="Net 30">Net 30</option>
              <option value="Net 60">Net 60</option>
              <option value="Net 90">Net 90</option>
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={2}
              placeholder="Optional notes for the invoice..."
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Auto-send Options */}
          <div className="bg-slate-700/50 rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-medium text-slate-300 mb-2">Automation Options</h3>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="auto_send"
                checked={formData.auto_send}
                onChange={handleChange}
                className="w-4 h-4 rounded bg-slate-600 border-slate-500 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <div className="text-sm text-white flex items-center gap-1">
                  <Send size={14} />
                  Auto-send invoices
                </div>
                <div className="text-xs text-slate-400">
                  Automatically send invoices when generated (otherwise created as draft)
                </div>
              </div>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="send_via_square"
                checked={formData.send_via_square}
                onChange={handleChange}
                disabled={!formData.auto_send}
                className="w-4 h-4 rounded bg-slate-600 border-slate-500 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
              />
              <div className={!formData.auto_send ? 'opacity-50' : ''}>
                <div className="text-sm text-white flex items-center gap-1">
                  <CreditCard size={14} />
                  Send via Square
                </div>
                <div className="text-xs text-slate-400">
                  Create Square invoice with payment link
                </div>
              </div>
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
            >
              {saving ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  {isEditing ? 'Update' : 'Create'} Recurring Invoice
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RecurringInvoiceModal;
